import React from 'react';
import './RecipePrint.css';

const groupByCategory = (items, key = 'category_name') =>
    (items || []).reduce((acc, item) => {
        const cat = item[key] || 'Uncategorized';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {});

function parseFraction(str) {
    if (!str) return null;
    const trimmed = str.trim();
    const mixed = trimmed.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) return parseInt(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
    const frac = trimmed.match(/^(\d+)\/(\d+)$/);
    if (frac) return parseInt(frac[1]) / parseInt(frac[2]);
    const num = parseFloat(trimmed);
    return isNaN(num) ? null : num;
}

function toFractionString(value) {
    const FRACTIONS = [
        [1, '1'], [3/4, '3/4'], [2/3, '2/3'], [1/2, '1/2'],
        [1/3, '1/3'], [1/4, '1/4'], [1/8, '1/8'],
    ];
    const whole = Math.floor(value);
    const remainder = value - whole;
    if (remainder < 0.05) return whole > 0 ? `${whole}` : '0';
    let closest = FRACTIONS[0];
    let minDiff = Math.abs(remainder - FRACTIONS[0][0]);
    for (const f of FRACTIONS) {
        const diff = Math.abs(remainder - f[0]);
        if (diff < minDiff) { minDiff = diff; closest = f; }
    }
    return whole > 0 ? `${whole} ${closest[1]}` : closest[1];
}

function scaleQuantity(quantity, multiplier) {
    if (!quantity || multiplier === 1) return quantity;
    const num = parseFraction(quantity);
    if (num === null) return quantity;
    const scaled = num * multiplier;
    if (Math.abs(scaled - Math.round(scaled)) < 0.05) return `${Math.round(scaled)}`;
    return toFractionString(scaled);
}

const RecipePrint = React.forwardRef(({ recipe, multiplier = 1, includePhoto = true, singleColumn = false, fontSize = 'normal', preview = false }, ref) => {
    if (!recipe) return null;

    const groupedIngredients = groupByCategory(recipe.ingredients);
    const groupedDirections = groupByCategory(recipe.directions);

    const directionEntries = Object.entries(groupedDirections).sort(([a], [b]) => {
        if (a === 'Uncategorized') return 1;
        if (b === 'Uncategorized') return -1;
        return a.localeCompare(b);
    });

    const meta = [
        recipe.prep_time && `Prep Time: ${recipe.prep_time}`,
        recipe.recipe_yield && `Yield: ${recipe.recipe_yield}`,
        multiplier > 1 && `Scaled: ${multiplier}x`,
    ].filter(Boolean).join('  ·  ');

    const escapedTitle = (recipe.title || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const rootClass = [
        'print-root',
        'print-active',
        fontSize === 'small' ? 'print-font-small' : fontSize === 'large' ? 'print-font-large' : '',
        preview ? 'print-preview-mode' : '',
    ].filter(Boolean).join(' ');

    return (
        <div ref={ref} className={rootClass}>
            <style>{`
                @media print {
                    @page {
                        @bottom-left {
                            content: "Sposito Family Cookbook  ·  ${escapedTitle}";
                            font-family: Georgia, serif;
                            font-size: 8.5pt;
                            color: #999;
                            border-top: 0.5pt solid #ddd;
                            padding-top: 4pt;
                        }
                    }
                    @page :first {
                        @bottom-left { content: none; border-top: none; }
                    }
                }
            `}</style>
            {/* Header */}
            <div className="print-header">
                <span>Sposito Family Cookbook</span>
            </div>

            {/* Title block + image */}
            <div className="print-title-block">
                <div className="print-title-text">
                    <h1 className="print-title">{recipe.title}</h1>
                    {(recipe.authors?.length > 0 || recipe.author) && (
                        <p className="print-author">
                            Recipe Courtesy of {recipe.authors?.length > 0
                                ? recipe.authors.map((a, i, arr) => (
                                    <span key={a.id}>
                                        {a.display_name}
                                        {a.avatar_url && <img src={a.avatar_url} alt={a.display_name} style={{ width: 18, height: 18, borderRadius: '50%', verticalAlign: 'middle', marginLeft: 4, position: 'relative', top: 2 }} />}
                                        {i < arr.length - 2 ? ', ' : i === arr.length - 2 ? ' & ' : ''}
                                    </span>
                                ))
                                : <>
                                    {recipe.author}
                                    {recipe.author_profile?.avatar_url && <img src={recipe.author_profile.avatar_url} alt={recipe.author} style={{ width: 18, height: 18, borderRadius: '50%', verticalAlign: 'middle', marginLeft: 6, position: 'relative', top: 2 }} />}
                                  </>
                            }
                        </p>
                    )}
                    {meta && <p className="print-meta">{meta}</p>}
                </div>
                {includePhoto && recipe.download_image_url && (
                    <img
                        className="print-image"
                        src={recipe.download_image_url}
                        alt={recipe.title}
                    />
                )}
            </div>

            <hr className="print-divider" />

            {/* Description */}
            {recipe.recipe_description && (
                <div className="print-section">
                    <h2 className="print-section-title">Description</h2>
                    <p className="print-body">{recipe.recipe_description}</p>
                </div>
            )}

            {/* Two-column: ingredients left, directions right */}
            <div className={singleColumn ? 'print-columns print-columns--single' : 'print-columns'}>
                {/* Ingredients */}
                {recipe.ingredients?.length > 0 && (
                    <div className="print-col-ingredients">
                        <h2 className="print-section-title">Ingredients</h2>
                        {Object.entries(groupedIngredients).map(([category, ings]) => (
                            <div key={category} className="print-category-block">
                                {category !== 'Uncategorized' && (
                                    <h3 className="print-category-title">{category}</h3>
                                )}
                                <ul className="print-list">
                                    {ings.map((ing, i) => (
                                        <li key={i}>
                                            {[scaleQuantity(ing.quantity, multiplier), ing.unit, ing.ingredient].filter(Boolean).join(' ')}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}

                {/* Directions */}
                {recipe.directions?.length > 0 && (
                    <div className="print-col-directions">
                        <h2 className="print-section-title">Directions</h2>
                        {directionEntries.map(([category, dirs]) => {
                            const sorted = [...dirs].sort(
                                (a, b) => (a.direction_order ?? 0) - (b.direction_order ?? 0)
                            );
                            return (
                                <div key={category} className="print-category-block">
                                    {category !== 'Uncategorized' && (
                                        <h3 className="print-category-title">{category}</h3>
                                    )}
                                    <ol className="print-list">
                                        {sorted.map((dir, i) => (
                                            <li key={i}>{dir.direction}</li>
                                        ))}
                                    </ol>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="print-footer">
                <span>{recipe.title}</span>
                <span className="print-page-number" />
            </div>
        </div>
    );
});

RecipePrint.displayName = 'RecipePrint';
export default RecipePrint;
